import type { openPublication } from "@msb/reader-bridge";
import { defineMessages, intlLocale, translator, type Locale } from "./i18n/index.ts";

/**
 * La fiche du livre (reprise de Studio) : ce que le manifeste dit du livre, ligne par ligne, seulement ce qui est
 * connu. Les métadonnées de Readium sont lues à plat pour rester testables sans publication.
 */
export interface BookMetadata {
  authors?: string[];
  translators?: string[];
  publishers?: string[];
  published?: Date;
  languages?: string[];
  identifier?: string;
  numberOfPages?: number;
  description?: string;
}

const messages = defineMessages(
  {
    author: ({ n }: { n: number }) => (n > 1 ? "Auteurs" : "Auteur"),
    translator: "Traduction",
    publisher: "Éditeur",
    year: "Parution",
    language: "Langue",
    pages: "Pages",
    isbn: "ISBN",
  },
  {
    author: ({ n }: { n: number }) => (n > 1 ? "Authors" : "Author"),
    translator: "Translation",
    publisher: "Publisher",
    year: "Published",
    language: "Language",
    pages: "Pages",
    isbn: "ISBN",
  },
);

/** Un ISBN lu dans l'identifiant du livre (« urn:isbn:978… », « 978-2-… ») ou, à défaut, dans le produit de la boutique. */
export function isbnOf(...candidates: Array<string | undefined>): string | undefined {
  for (const candidate of candidates) {
    const digits = candidate?.replace(/^urn:isbn:/i, "").replace(/[\s-]/g, "");
    if (digits && /^(97[89]\d{10}|\d{9}[\dX])$/i.test(digits)) return digits;
  }
  return undefined;
}

function languageName(code: string, locale: Locale): string {
  try {
    const name = new Intl.DisplayNames([intlLocale(locale)], { type: "language" }).of(code);
    return name ? name.charAt(0).toLocaleUpperCase(intlLocale(locale)) + name.slice(1) : code;
  } catch {
    return code;
  }
}

/** Le résumé de l'éditeur, sans balises HTML. */
export function plainDescription(description: string | undefined): string | undefined {
  if (!description) return undefined;
  const text = new DOMParser().parseFromString(description, "text/html").body.textContent ?? "";
  return text.replace(/\s+/g, " ").trim() || undefined;
}

export function bookDetails(metadata: BookMetadata, locale: Locale, productId?: string): Array<{ label: string; value: string }> {
  const t = translator(messages, locale);
  const list = (names: string[] | undefined) => names?.filter(Boolean).join(", ");
  const rows: Array<[string, string | undefined]> = [
    [t("author", { n: metadata.authors?.length ?? 0 }), list(metadata.authors)],
    [t("translator"), list(metadata.translators)],
    [t("publisher"), list(metadata.publishers)],
    [t("year"), metadata.published && !Number.isNaN(metadata.published.getTime()) ? String(metadata.published.getFullYear()) : undefined],
    [t("language"), metadata.languages?.length ? metadata.languages.map((code) => languageName(code, locale)).join(", ") : undefined],
    [t("pages"), metadata.numberOfPages ? String(metadata.numberOfPages) : undefined],
    [t("isbn"), isbnOf(metadata.identifier, productId)],
  ];
  return rows.filter((row): row is [string, string] => !!row[1]).map(([label, value]) => ({ label, value }));
}

type Publication = Awaited<ReturnType<typeof openPublication>>;

/** Ce que la fiche montre d'une publication : ses métadonnées à plat et l'adresse de sa couverture. */
export interface BookInfo {
  metadata: BookMetadata;
  cover?: string;
}

export function bookOf(publication: Publication, manifestUrl: string): BookInfo {
  const m = publication.metadata;
  const names = (contributors: { items: Array<{ name: { getTranslation(): string } }> } | undefined) =>
    contributors?.items.map((c) => c.name.getTranslation()).filter(Boolean);
  const cover = publication.getCover()?.href;
  let coverUrl: string | undefined;
  try {
    coverUrl = cover ? new URL(cover, manifestUrl).href : undefined;
  } catch {
    coverUrl = undefined;
  }
  return {
    metadata: {
      authors: names(m.authors),
      translators: names(m.translators),
      publishers: names(m.publishers),
      published: m.published,
      languages: m.languages,
      identifier: m.identifier,
      numberOfPages: m.numberOfPages,
      description: m.description,
    },
    cover: coverUrl,
  };
}
